import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useCalendarData } from "@/hooks/useCalendarData";
import { useCurrentTime } from "@/hooks/useCurrentTime";
import { useAutoScroll } from "@/hooks/useAutoScroll";
import { CalendarHeader } from "./CalendarHeader";
import { TimelineGrid } from "./TimelineGrid";
import { EventDetailModal } from "./EventDetailModal";
import { BookingModal } from "./BookingModal";
import type { MoveDragGhost } from "./RoomColumn";
import { MEETING_ROOMS, CALENDAR_CONFIG } from "@/config/rooms";
import { getTotalGridHeight, formatTimeShort } from "@/lib/calendar-utils";
import { isMoveCandidate, validateMove } from "@/lib/move-validation";
import type { CalendarEvent, MeetingRoom } from "@/types/calendar";
import type { AuthUser } from "@/hooks/useAuth";

const SLOT_MIN = 15;
const SLOT_HEIGHT_PX = CALENDAR_CONFIG.hourHeight / 4;
const DRAG_THRESHOLD_PX = 5;
const TOTAL_SLOTS = (CALENDAR_CONFIG.endHour - CALENDAR_CONFIG.startHour) * 4;

interface SelectedEvent {
  event: CalendarEvent;
  room: MeetingRoom;
}

interface MoveDragState {
  event: CalendarEvent;
  fromRoomId: string;
  durationMs: number;
  offsetWithinEventPx: number;
  startClientX: number;
  startClientY: number;
  pointerClientX: number;
  pointerClientY: number;
  isActive: boolean;
  target: MoveTarget | null;
}

interface MoveTarget {
  roomId: string;
  startDate: Date;
  endDate: Date;
  topPx: number;
  valid: boolean;
  reason?: string;
}

interface FeedbackMessage {
  type: "success" | "error";
  text: string;
}

function isWorkingHours(date: Date): boolean {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const timeValue = hours * 60 + minutes;
  return timeValue >= 540 && timeValue < 1230;
}

interface MeetingRoomCalendarProps {
  user?: AuthUser;
  onSignOut?: () => void;
}

export function MeetingRoomCalendar({ user, onSignOut }: MeetingRoomCalendarProps = {}) {
  const {
    data,
    loading,
    error,
    lastRefresh,
    refetch,
    insertEventLocal,
    moveEventLocal,
  } = useCalendarData();
  const currentTime = useCurrentTime();
  const scrollRef = useAutoScroll(currentTime, !loading);
  const [selectedEvent, setSelectedEvent] = useState<SelectedEvent | null>(null);
  interface BookingContext {
    roomId?: string | null;
    start?: Date;
    end?: Date;
  }
  const [bookingContext, setBookingContext] = useState<BookingContext | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [workingHoursOnly, setWorkingHoursOnly] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("workingHoursOnly") !== "false";
    }
    return true;
  });

  const [moveDrag, setMoveDrag] = useState<MoveDragState | null>(null);
  const [feedback, setFeedback] = useState<FeedbackMessage | null>(null);

  const moveDragRef = useRef<MoveDragState | null>(null);
  useEffect(() => {
    moveDragRef.current = moveDrag;
  }, [moveDrag]);

  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const currentTimeRef = useRef(currentTime);
  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  const suppressClickUntilRef = useRef<number>(0);

  const columnRefsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const registerColumnRef = useCallback(
    (roomId: string, el: HTMLDivElement | null) => {
      if (el) columnRefsRef.current.set(roomId, el);
      else columnRefsRef.current.delete(roomId);
    },
    [],
  );

  const isBlackScreen = workingHoursOnly && !isWorkingHours(currentTime);

  useEffect(() => {
    localStorage.setItem("workingHoursOnly", String(workingHoursOnly));
  }, [workingHoursOnly]);

  const gridHeight = getTotalGridHeight();

  const handleEventClick = useCallback(
    (event: CalendarEvent, room: MeetingRoom) => {
      if (suppressClickUntilRef.current > Date.now()) {
        console.log(`event_click_suppressed eventId=${event.id} (drag just completed)`);
        return;
      }
      setSelectedEvent({ event, room });
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  const handleOpenBooking = useCallback(
    (ctx: { roomId?: string | null; start?: Date; end?: Date } = {}) => {
      setBookingContext(ctx);
      setBookingOpen(true);
    },
    [],
  );

  const handleSlotSelect = useCallback(
    (roomId: string, startISO: string, endISO: string) => {
      handleOpenBooking({
        roomId,
        start: new Date(startISO),
        end: new Date(endISO),
      });
    },
    [handleOpenBooking],
  );

  const handleCloseBooking = useCallback(() => {
    setBookingOpen(false);
    setBookingContext(null);
  }, []);

  const isMovableEvent = useCallback(
    (event: CalendarEvent) => isMoveCandidate(event, user?.email),
    [user?.email],
  );

  const computeMoveTarget = useCallback(
    (state: MoveDragState, clientX: number, clientY: number): MoveTarget | null => {
      let targetRoomId: string | null = null;
      let targetRect: DOMRect | null = null;
      for (const [roomId, el] of columnRefsRef.current.entries()) {
        const rect = el.getBoundingClientRect();
        if (clientX >= rect.left && clientX <= rect.right) {
          targetRoomId = roomId;
          targetRect = rect;
          break;
        }
      }
      if (!targetRoomId || !targetRect) return null;

      const eventTopInColumn = clientY - state.offsetWithinEventPx - targetRect.top;
      const durationSlots = Math.max(
        1,
        Math.round(state.durationMs / 60000 / SLOT_MIN),
      );
      let slotIndex = Math.round(eventTopInColumn / SLOT_HEIGHT_PX);
      slotIndex = Math.max(0, Math.min(slotIndex, TOTAL_SLOTS - durationSlots));

      const startTotalMin = CALENDAR_CONFIG.startHour * 60 + slotIndex * SLOT_MIN;
      const startDate = new Date(currentTimeRef.current);
      startDate.setHours(
        Math.floor(startTotalMin / 60),
        startTotalMin % 60,
        0,
        0,
      );
      const endDate = new Date(startDate.getTime() + durationSlots * SLOT_MIN * 60 * 1000);

      const result = validateMove({
        event: state.event,
        fromRoomId: state.fromRoomId,
        toRoomId: targetRoomId,
        newStart: startDate,
        newEnd: endDate,
        eventsAtTarget: dataRef.current.get(targetRoomId) || [],
        currentUserEmail: user?.email,
        currentTime: currentTimeRef.current,
      });

      return {
        roomId: targetRoomId,
        startDate,
        endDate,
        topPx: slotIndex * SLOT_HEIGHT_PX,
        valid: result.ok,
        reason: result.reason,
      };
    },
    [user?.email],
  );

  const commitMove = useCallback(
    async (state: MoveDragState) => {
      if (!state.target || !state.event.organizerEmail) {
        console.log("commit_move_skip reason=no_target_or_organizer");
        return;
      }
      const { event, fromRoomId, target } = state;
      const originalStart = event.start;
      const originalEnd = event.end;

      console.log(
        `commit_move_optimistic eventId=${event.id} from=${fromRoomId} to=${target.roomId} start=${target.startDate.toISOString()}`,
      );
      moveEventLocal(
        fromRoomId,
        target.roomId,
        event.id,
        target.startDate,
        target.endDate,
      );

      try {
        const res = await fetch("/api/event/move", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({
            eventId: event.id,
            fromRoomId,
            toRoomId: target.roomId,
            startISO: target.startDate.toISOString(),
            endISO: target.endDate.toISOString(),
            organizerEmail: event.organizerEmail,
          }),
        });
        const json = await res.json();
        if (!res.ok) {
          console.error(`commit_move_failed status=${res.status}`, json);
          moveEventLocal(
            target.roomId,
            fromRoomId,
            event.id,
            originalStart,
            originalEnd,
          );
          setFeedback({
            type: "error",
            text: json?.error || `이동 실패 (${res.status})`,
          });
          return;
        }
        if (json.roomDeclined) {
          console.error("commit_move_room_declined", json);
          moveEventLocal(
            target.roomId,
            fromRoomId,
            event.id,
            originalStart,
            originalEnd,
          );
          setFeedback({
            type: "error",
            text: json.message || "이동한 회의실이 사용 중입니다.",
          });
          return;
        }
        console.log(`commit_move_ok eventId=${event.id}`);
        setFeedback({ type: "success", text: "이동 완료" });
        setTimeout(() => refetch(), 4000);
        setTimeout(() => refetch(), 12000);
      } catch (err) {
        console.error("commit_move_error", err);
        moveEventLocal(
          target.roomId,
          fromRoomId,
          event.id,
          originalStart,
          originalEnd,
        );
        setFeedback({
          type: "error",
          text: err instanceof Error ? err.message : "네트워크 오류",
        });
      }
    },
    [moveEventLocal, refetch],
  );

  const handleMoveDragStart = useCallback(
    (
      event: CalendarEvent,
      fromRoomId: string,
      e: React.PointerEvent<HTMLDivElement>,
    ) => {
      if (!isMoveCandidate(event, user?.email)) {
        console.log(`move_drag_blocked reason=not_movable eventId=${event.id}`);
        return;
      }
      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      const offsetWithinEventPx = e.clientY - rect.top;
      console.log(
        `move_drag_start eventId=${event.id} fromRoomId=${fromRoomId} offsetPx=${offsetWithinEventPx.toFixed(1)}`,
      );
      setMoveDrag({
        event,
        fromRoomId,
        durationMs: event.end.getTime() - event.start.getTime(),
        offsetWithinEventPx,
        startClientX: e.clientX,
        startClientY: e.clientY,
        pointerClientX: e.clientX,
        pointerClientY: e.clientY,
        isActive: false,
        target: null,
      });
    },
    [user?.email],
  );

  useEffect(() => {
    if (!moveDrag) return;

    const handlePointerMove = (e: PointerEvent) => {
      setMoveDrag((prev) => {
        if (!prev) return prev;
        const dx = e.clientX - prev.startClientX;
        const dy = e.clientY - prev.startClientY;
        const dist = Math.hypot(dx, dy);
        const isActive = prev.isActive || dist > DRAG_THRESHOLD_PX;
        const target = isActive
          ? computeMoveTarget(prev, e.clientX, e.clientY)
          : prev.target;
        return {
          ...prev,
          pointerClientX: e.clientX,
          pointerClientY: e.clientY,
          isActive,
          target,
        };
      });
    };

    const handlePointerUp = (_e: PointerEvent) => {
      const current = moveDragRef.current;
      setMoveDrag(null);
      if (!current) return;
      if (current.isActive) {
        suppressClickUntilRef.current = Date.now() + 300;
        if (current.target && current.target.valid) {
          void commitMove(current);
        } else {
          console.log(
            `move_drag_cancel reason=${current.target ? "invalid_target" : "no_target"}`,
          );
          if (current.target?.reason) {
            setFeedback({ type: "error", text: current.target.reason });
          }
        }
      } else {
        console.log("move_drag_release_as_click");
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [moveDrag !== null, computeMoveTarget, commitMove]);

  // Auto-dismiss feedback toast
  useEffect(() => {
    if (!feedback) return;
    const ms = feedback.type === "success" ? 1800 : 3500;
    const id = setTimeout(() => setFeedback(null), ms);
    return () => clearTimeout(id);
  }, [feedback]);

  const moveDragGhost: MoveDragGhost | null = useMemo(() => {
    if (!moveDrag || !moveDrag.isActive || !moveDrag.target) return null;
    const durationSlots = Math.max(
      1,
      Math.round(moveDrag.durationMs / 60000 / SLOT_MIN),
    );
    return {
      topPx: moveDrag.target.topPx,
      heightPx: durationSlots * SLOT_HEIGHT_PX,
      valid: moveDrag.target.valid,
      reason: moveDrag.target.reason,
      label: moveDrag.event.summary,
      timeLabel: `${formatTimeShort(moveDrag.target.startDate)} – ${formatTimeShort(moveDrag.target.endDate)}`,
    };
  }, [moveDrag]);

  const moveDragTargetRoomId = moveDrag?.isActive ? moveDrag.target?.roomId ?? null : null;
  const draggingEventId = moveDrag?.event.id ?? null;
  const isMoveInteractionActive = moveDrag !== null;

  if (loading && data.size === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <div className="text-muted-foreground">Loading calendars...</div>
        </div>
      </div>
    );
  }

  if (isBlackScreen) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-black text-white">
        <div className="text-center">
          <div className="text-6xl mb-4">🌙</div>
          <div className="text-2xl font-light mb-2">업무 시간이 아닙니다</div>
          <div className="text-gray-500 mb-8">09:00 ~ 20:30</div>
          <button
            onClick={() => setWorkingHoursOnly(false)}
            className="text-sm text-gray-600 hover:text-gray-400 transition-colors"
          >
            화면 켜기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col h-screen max-w-6xl mx-auto bg-background"
      style={isMoveInteractionActive ? { cursor: "grabbing" } : undefined}
    >
      <CalendarHeader
        rooms={MEETING_ROOMS}
        lastRefresh={lastRefresh}
        onRefresh={refetch}
        loading={loading}
        workingHoursOnly={workingHoursOnly}
        onToggleWorkingHours={() => setWorkingHoursOnly(!workingHoursOnly)}
        user={user}
        onSignOut={onSignOut}
      />

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <TimelineGrid
          rooms={MEETING_ROOMS}
          eventsMap={data}
          currentTime={currentTime}
          gridHeight={gridHeight}
          onEventClick={handleEventClick}
          onSlotSelect={handleSlotSelect}
          registerColumnRef={registerColumnRef}
          moveDragTargetRoomId={moveDragTargetRoomId}
          moveDragGhost={moveDragGhost}
          draggingEventId={draggingEventId}
          isMovableEvent={isMovableEvent}
          onMoveDragStart={handleMoveDragStart}
        />
      </div>

      {error && (
        <div className="fixed bottom-4 right-4 bg-destructive text-destructive-foreground px-4 py-2 rounded-md shadow-lg text-sm">
          {error}
        </div>
      )}

      {feedback && (
        <div
          className={`fixed bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-md shadow-lg text-sm z-50 ${
            feedback.type === "success"
              ? "bg-green-600 text-white"
              : "bg-destructive text-destructive-foreground"
          }`}
        >
          {feedback.text}
        </div>
      )}

      <EventDetailModal
        event={selectedEvent?.event ?? null}
        room={selectedEvent?.room ?? null}
        onClose={handleCloseModal}
      />

      {bookingOpen && (
        <BookingModal
          rooms={MEETING_ROOMS}
          defaultRoomId={bookingContext?.roomId ?? null}
          defaultStart={bookingContext?.start}
          defaultEnd={bookingContext?.end}
          onClose={handleCloseBooking}
          onBooked={(roomId, event) => {
            insertEventLocal(roomId, event);
            setTimeout(() => refetch(), 4000);
            setTimeout(() => refetch(), 12000);
          }}
        />
      )}
    </div>
  );
}
